/**
 * Tipi delle statistiche di squadra (V32–V34) e ordinamento delle righe.
 * I numeri arrivano gia calcolati dal server: qui si decide solo in che
 * ordine mostrarli.
 */

import type { RuoloGiocatore } from '@calcio/shared';

export interface Competizione {
  id: string;
  nome: string;
}

export interface StatisticheSquadra {
  competizioni: Competizione[];
  giocate: number;
  vinte: number;
  pareggiate: number;
  perse: number;
  golFatti: number;
  golSubiti: number;
  mediaGolFatti: number | null;
  mediaGolSubiti: number | null;
  perPeriodo: { periodo: number; golFatti: number; golSubiti: number }[];
  /** I totali degli eventi dei nostri giocatori. */
  eventi: ContatoriEventi;
  /** La somma dei minuti dei giocatori divisa per quanti vanno in campo, partita per partita. */
  minuti: number;
  /** Assente per chi non legge i voti. */
  votoMedio?: number | null;
  /** Tutta la stagione, senza filtro; assente per chi non legge gli allenamenti. */
  allenamenti?: { svolti: number; annullati: number; percentuale: number | null };
}

interface Giocatore {
  id: string;
  nome: string;
  cognome: string;
  numero: number | null;
  ruolo: RuoloGiocatore;
}

export interface ContatoriEventi {
  gol: number;
  autogol: number;
  assist: number;
  tiriSpecchio: number;
  tiriFuori: number;
  passaggiCompletati: number;
  passaggiSbagliati: number;
  palleConquistate: number;
  pallePerse: number;
  gialli: number;
  rossi: number;
}

export interface RigaClassifica extends Giocatore, ContatoriEventi {
  presenze: number;
  daTitolare: number;
  minuti: number;
  /** Assente per chi non legge i voti. */
  votoMedio?: number | null;
}

export interface ClassificheGiocatori {
  competizioni: Competizione[];
  giocatori: RigaClassifica[];
}

export interface RigaPresenze extends Giocatore {
  disponibili: number;
  presenti: number;
  assenti: number;
  infortunati: number;
  nonRilevati: number;
  percentuale: number | null;
}

export interface StatistichePresenze {
  svolti: number;
  annullati: number;
  giocatori: RigaPresenze[];
}

/** Il filtro per competizione di V32 e V33 vive nella query string. */
export function competizioneDaSearch(search: string): string | undefined {
  return new URLSearchParams(search).get('competizione') || undefined;
}

export const queryCompetizione = (competizione: string | undefined): string =>
  competizione ? `?competizione=${encodeURIComponent(competizione)}` : '';

export type Verso = 'asc' | 'desc';
export interface Ordine<K extends string> {
  colonna: K | 'giocatore';
  verso: Verso;
}

const confronto = new Intl.Collator('it', { sensitivity: 'base' });
type ConNome = Pick<Giocatore, 'nome' | 'cognome'>;
const perNome = (a: ConNome, b: ConNome) =>
  confronto.compare(a.cognome, b.cognome) || confronto.compare(a.nome, b.nome);

/**
 * Righe ordinate per una colonna numerica o per giocatore. I valori mancanti
 * (media voto o percentuale senza dati) stanno sempre in fondo, in qualunque
 * verso; a parita decide il cognome.
 */
export function ordina<R extends ConNome, K extends string & keyof R>(
  righe: readonly R[],
  { colonna, verso }: Ordine<K>,
): R[] {
  const segno = verso === 'asc' ? 1 : -1;
  return [...righe].sort((a, b) => {
    if (colonna === 'giocatore') return segno * perNome(a, b);
    const x = a[colonna] as number | null | undefined;
    const y = b[colonna] as number | null | undefined;
    if (x == null || y == null) {
      if (x == null && y == null) return perNome(a, b);
      return x == null ? 1 : -1;
    }
    return segno * (x - y) || perNome(a, b);
  });
}

/**
 * Il clic su un'intestazione: la stessa colonna inverte il verso, una
 * colonna nuova parte dai numeri piu alti e dalla A per il giocatore, salvo
 * un verso iniziale diverso (il numero di maglia parte dal piu basso).
 */
export function prossimoOrdine<K extends string>(
  attuale: Ordine<K>,
  colonna: K | 'giocatore',
  iniziale: Verso = colonna === 'giocatore' ? 'asc' : 'desc',
): Ordine<K> {
  if (attuale.colonna === colonna) {
    return { colonna, verso: attuale.verso === 'asc' ? 'desc' : 'asc' };
  }
  return { colonna, verso: iniziale };
}

export const numero = (v: number | null | undefined): string =>
  v == null ? '—' : v.toLocaleString('it-IT');

export const percentuale = (v: number | null): string =>
  v == null ? '—' : `${v.toLocaleString('it-IT')}%`;

/** La parte `di` sul totale `di + contro`, in percentuale a un decimale; null senza nessuno dei due. */
export function quota(di: number, contro: number): number | null {
  const totale = di + contro;
  return totale === 0 ? null : Math.round((di / totale) * 1000) / 10;
}

/** Presenti sui rilevati di tutta la rosa, con la regola della percentuale di ciascuno. */
export function partecipazione(righe: readonly RigaPresenze[]): number | null {
  let presenti = 0;
  let altri = 0;
  for (const r of righe) {
    presenti += r.presenti;
    altri += r.assenti + r.infortunati;
  }
  return quota(presenti, altri);
}
